// Professional Trading Utilities
export const calculateRSI = (prices, period = 14) => {
  if (prices.length < period + 1) return null;
  
  let gains = 0;
  let losses = 0;
  
  for (let i = 1; i <= period; i++) {
    const change = prices[i] - prices[i - 1];
    if (change > 0) {
      gains += change;
    } else {
      losses -= change;
    }
  }
  
  const avgGain = gains / period;
  const avgLoss = losses / period;
  const rs = avgGain / avgLoss;
  const rsi = 100 - (100 / (1 + rs));
  
  return rsi;
};

export const calculateMACD = (prices, fastPeriod = 12, slowPeriod = 26, signalPeriod = 9) => {
  if (prices.length < slowPeriod) return { macd: null, signal: null, histogram: null };
  
  const ema12 = calculateEMA(prices, fastPeriod);
  const ema26 = calculateEMA(prices, slowPeriod);
  
  if (!ema12 || !ema26) return { macd: null, signal: null, histogram: null };
  
  const macdLine = ema12 - ema26;
  const signalLine = calculateEMA([macdLine], signalPeriod);
  const histogram = macdLine - signalLine;
  
  return { macd: macdLine, signal: signalLine, histogram };
};

export const calculateEMA = (prices, period) => {
  if (prices.length < period) return null;
  
  const multiplier = 2 / (period + 1);
  let ema = prices[0];
  
  for (let i = 1; i < prices.length; i++) {
    ema = (prices[i] * multiplier) + (ema * (1 - multiplier));
  }
  
  return ema;
};

export const calculateSMA = (prices, period) => {
  if (prices.length < period) return null;
  
  const sum = prices.slice(-period).reduce((acc, price) => acc + price, 0);
  return sum / period;
};

export const calculateVolatility = (prices, period = 24) => {
  if (prices.length < period) return null;
  
  const returns = [];
  for (let i = 1; i < prices.length; i++) {
    returns.push((prices[i] - prices[i - 1]) / prices[i - 1]);
  }
  
  const mean = returns.reduce((acc, ret) => acc + ret, 0) / returns.length;
  const variance = returns.reduce((acc, ret) => acc + Math.pow(ret - mean, 2), 0) / returns.length;
  
  return Math.sqrt(variance) * 100; // Return as percentage
};

export const calculateBeta = (coinPrices, btcPrices) => {
  if (coinPrices.length !== btcPrices.length || coinPrices.length < 2) return null;
  
  const coinReturns = [];
  const btcReturns = [];
  
  for (let i = 1; i < coinPrices.length; i++) {
    coinReturns.push((coinPrices[i] - coinPrices[i - 1]) / coinPrices[i - 1]);
    btcReturns.push((btcPrices[i] - btcPrices[i - 1]) / btcPrices[i - 1]);
  }
  
  const coinMean = coinReturns.reduce((acc, ret) => acc + ret, 0) / coinReturns.length;
  const btcMean = btcReturns.reduce((acc, ret) => acc + ret, 0) / btcReturns.length;
  
  let numerator = 0;
  let denominator = 0;
  
  for (let i = 0; i < coinReturns.length; i++) {
    numerator += (coinReturns[i] - coinMean) * (btcReturns[i] - btcMean);
    denominator += Math.pow(btcReturns[i] - btcMean, 2);
  }
  
  return denominator !== 0 ? numerator / denominator : null;
};

export const calculateATHDistance = (currentPrice, athPrice) => {
  if (!athPrice || athPrice === 0) return null;
  return ((currentPrice - athPrice) / athPrice) * 100;
};

export const formatCurrency = (value, currency = 'USD') => {
  if (value === null || value === undefined) return 'N/A';
  
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  
  return formatter.format(value);
};

export const formatPercentage = (value, decimals = 2) => {
  if (value === null || value === undefined) return 'N/A';
  
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(decimals)}%`;
};

export const formatLargeNumber = (value, decimals = 2) => {
  if (value === null || value === undefined) return 'N/A';
  
  if (value >= 1e12) {
    return `${(value / 1e12).toFixed(decimals)}T`;
  } else if (value >= 1e9) {
    return `${(value / 1e9).toFixed(decimals)}B`;
  } else if (value >= 1e6) {
    return `${(value / 1e6).toFixed(decimals)}M`;
  } else if (value >= 1e3) {
    return `${(value / 1e3).toFixed(decimals)}K`;
  }
  
  return value.toFixed(decimals);
};

export const getColorByValue = (value, isPositive = true) => {
  if (value === null || value === undefined) return '#888';
  
  if (isPositive) {
    return value >= 0 ? '#00ff88' : '#ff4444';
  } else {
    return value >= 0 ? '#ff4444' : '#00ff88';
  }
};

export const calculateVolumeMarketCapRatio = (volume, marketCap) => {
  if (!volume || !marketCap || marketCap === 0) return null;
  return (volume / marketCap) * 100;
};

export const calculateCirculatingSupplyRatio = (circulating, total) => {
  if (!circulating || !total || total === 0) return null;
  return (circulating / total) * 100;
}; 